import React from 'react'
import { PropTypes } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '../../../../components/mui/Core/index'
import LinkButton from '../../../../components/LinkButton/link-button'
import Constants from '../../../../constants/en.json'
import { fileTypes } from '../../../../constants/file-types'
import { FileUploadIcon } from '../../../../components/mui/Icons'
import { hasUserPermission } from '../../../../utils/user-utils'
import { userPermissionConstants } from '../../../../constants/user-permission-constants'
import { fetchDownloadOrderDocument } from '../../OrderDetails/order-document-slice'

function ShippingDocumentsCard({
  orderDetails,
  handleGetReport,
  reportDownloadStatus,
  handleOpenUploadDocumentModal,
  orderDocuments,
}) {
  const dispatch = useDispatch()
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const hasUploadOrderDocumentAccess = hasUserPermission({
    permissionCode: userPermissionConstants.UPLOAD_ORDER_DOCUMENT,
    permissions: authenticatedUser?.permissions,
  })

  const bol = orderDetails?.bolDocument // Bill of landing document
  const bolRetention = orderDetails?.bolRetention // Bill of landing retention
  const packList = orderDetails?.mplDocument // Master packing list document
  const mplRetention = orderDetails?.mplRetention // Master packing list retention

  const headerStyle = { py: hasUploadOrderDocumentAccess ? 1.5 : 3 }
  const bodyStyle = { py: 2 }
  const mediumBottomMarginStyle = { mb: 3 }
  const mediumTopMarginStyle = { mt: 1 }

  const handleDownloadOrderDocument = async (documentType) => {
    const orderId = orderDetails?.wmsOrderNumber

    try {
      const action = await dispatch(fetchDownloadOrderDocument({ orderId, documentType }))
      if (!fetchDownloadOrderDocument.fulfilled.match(action)) return
      const fileUrl = action.payload?.data
      if (!fileUrl) return
      window.open(fileUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      // Nothing to do here
    }
  }

  return (
    <Card variant='outlined' sx={{ height: '100%' }}>
      <CardContent sx={headerStyle}>
        <Grid container alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>{Constants.orders.order_details.DOCUMENTS}</Typography>
          {hasUploadOrderDocumentAccess && (
            <IconButton size='small' onClick={handleOpenUploadDocumentModal}>
              <FileUploadIcon />
            </IconButton>
          )}
        </Grid>
      </CardContent>
      <Divider />
      <CardContent sx={bodyStyle}>
        <Box sx={{ overflow: 'auto', height: 200 }}>
          {/* Bill of Lading */}
          <Grid container spacing={2} sx={mediumBottomMarginStyle}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6'>{Constants.orders.order_details.BILL_OF_LADING}</Typography>
              <Typography variant='body1'>
                {bolRetention === 'PENDING' ? '(Time remaining pending)' : `(${bolRetention})`}
              </Typography>
              {bol && bol === Constants.orders.order_details.UNAVAILABLE && (
                <Typography variant='body1' sx={mediumTopMarginStyle}>
                  {Constants.orders.order_details.UNAVAILABLE}
                </Typography>
              )}
              {bol && bol !== Constants.orders.order_details.UNAVAILABLE && (
                <Typography variant='body1'>
                  <LinkButton
                    linkText={bol}
                    handleLinkClick={() => handleGetReport(bol, fileTypes.download.BOL)}
                  />
                </Typography>
              )}
              {!bol && (
                <Typography variant='body1' sx={mediumTopMarginStyle}>
                  {Constants.orders.order_details.PENDING}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Master Packing List */}
          <Grid container spacing={2} sx={mediumBottomMarginStyle}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h6'>
                {Constants.orders.order_details.MASTER_PACKING_LIST}
              </Typography>
              <Typography variant='body1'>
                {mplRetention === 'PENDING' ? '(Time remaining pending)' : `(${mplRetention})`}
              </Typography>
              {packList && (
                <Typography variant='body1'>
                  <LinkButton
                    linkText={packList}
                    handleLinkClick={() => handleGetReport(packList, fileTypes.download.MPL)}
                  />
                </Typography>
              )}
              {!packList && (
                <Typography variant='body1' sx={mediumTopMarginStyle}>
                  {Constants.orders.order_details.PENDING}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Order Documents */}
          {orderDocuments?.map((doc) => (
            <Grid
              container
              spacing={2}
              sx={mediumBottomMarginStyle}
              key={`${doc.documentType}-${doc.originalDocument}`}
            >
              <Grid size={{ xs: 12 }}>
                <Typography variant='h6'>{doc.documentType}</Typography>
                <Typography variant='body1'>({doc.retentionTime})</Typography>
                <Typography variant='body1'>
                  <LinkButton
                    linkText={doc.originalDocument}
                    handleLinkClick={() => handleDownloadOrderDocument(doc.documentType)}
                  />
                </Typography>
              </Grid>
            </Grid>
          ))}

          {/* Message for failed download */}
          <Grid container spacing={2} sx={mediumBottomMarginStyle}>
            {reportDownloadStatus === 'Empty File' && (
              <Grid size={{ xs: 12 }} sx={{ mt: 0, pt: 0 }}>
                <Typography variant='body1' sx={{ color: 'red !important' }}>
                  {Constants.orders.order_details.DOWNLOAD_FAILED}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  )
}

ShippingDocumentsCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  orderDetails: PropTypes.any.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  orderDocuments: PropTypes.any.isRequired,
  handleGetReport: PropTypes.func.isRequired,
  reportDownloadStatus: PropTypes.string.isRequired,
  handleOpenUploadDocumentModal: PropTypes.func.isRequired,
}

export default ShippingDocumentsCard
